// Pricing-margin editor card (moved here from the general settings page so
// all pricing knobs live together). Edits the admin-only `pricing_margin_pct`
// portal setting through the adminSettings slice with its own save button —
// independent of the matrix's per-row saves below it.
import { useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  Paper, Typography, TextField, Button, Stack, CircularProgress, Alert,
} from '@mui/material';
import {
  fetchAdminSettings, saveAdminSettings, clearSaveError,
} from '../../../store/admin-settings-slice.js';

const PricingMarginCard = ({ onDirtyChange }) => {
  const dispatch = useDispatch();
  const {
    settings, loading, saving, saveError,
  } = useSelector((s) => s.adminSettings);
  const [value, setValue] = useState('');

  useEffect(() => {
    if (!settings && !loading) dispatch(fetchAdminSettings());
  }, [settings, loading, dispatch]);

  // Seed once settings arrive (and after a save resyncs them).
  useEffect(() => {
    if (settings) setValue(String(settings.pricing_margin_pct ?? ''));
  }, [settings]);

  const parsed = Number(value);
  const valid = Number.isFinite(parsed) && parsed >= 0 && parsed < 1;
  const dirty = useMemo(
    () => settings && valid && parsed !== Number(settings.pricing_margin_pct),
    [settings, valid, parsed],
  );

  // Let the parent page fold this card's dirty state into its unsaved-changes
  // navigation guard.
  useEffect(() => {
    onDirtyChange?.(Boolean(dirty));
    return () => onDirtyChange?.(false);
  }, [dirty, onDirtyChange]);

  const handleChange = (e) => {
    if (saveError) dispatch(clearSaveError());
    setValue(e.target.value);
  };

  const handleSave = () => dispatch(saveAdminSettings({ pricing_margin_pct: parsed }));

  return (
    <Paper variant="outlined" sx={{ p: 3, mb: 3 }}>
      <Typography variant="h6" component="h2" sx={{ fontWeight: 700, mb: 1 }}>
        Price Estimate Buffer
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Admin-only. Fraction between 0 and 1 used to spread the point estimate
        into the public low–high range (e.g. 0.15 = ±15%).
      </Typography>
      {saveError && <Alert severity="error" sx={{ mb: 2 }}>{saveError}</Alert>}
      <Stack direction="row" spacing={2} sx={{ alignItems: 'center' }}>
        <TextField
          label="Buffer"
          type="number"
          value={value}
          onChange={handleChange}
          error={value !== '' && !valid}
          helperText={value !== '' && !valid ? 'Enter a number between 0 and 1.' : undefined}
          slotProps={{ htmlInput: { min: 0, max: 0.99, step: 0.01 } }}
          size="small"
          sx={{ maxWidth: 200 }}
        />
        <Button variant="contained" onClick={handleSave} disabled={!dirty || saving}>
          {saving ? <CircularProgress size={20} color="inherit" /> : 'Save Buffer'}
        </Button>
      </Stack>
    </Paper>
  );
};

export default PricingMarginCard;
